import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { AREA_CLS, Collapsible } from "@/shared/ui/Collapsible";
import { openExternalUrl } from "@/shared/openExternalUrl";
import { TrackingPanel } from "@/features/messaggi/components/TrackingPanel";
import { orderHasAllServiceSku } from "../../allserviceApi";
import { fetchOrderParcelCodes } from "../../api";
import type { Order } from "../../types";
import { type GenerateLabelClickOpts } from "../OrderCard";
import { RitiroRicambio } from "./RitiroRicambio";
import { ShippingEditor, type ShippingDraft } from "../ShippingEditor";
import { nomeCorriere } from "../../nomeCorriere";
import { EMPTY_DRAFT, haLdv, opzioniLdv } from "./azioni";
import { BTN_PRIMARY, BTN_SM, PANEL, TITLE, TITLE_NOTE } from "./stile";

type Collo = { kg: number; l: number; a: number; p: number };

/** I colli COME REGISTRATI: le misure salvate, o in mancanza quelle proposte
 *  dal listino fornitore. Stessa scaletta del seed di ``ShippingEditor`` — se
 *  divergessero, il riassunto chiuso direbbe un numero e l'editor un altro. */
function colliRegistrati(order: Order): Collo[] {
  if (order.packageDimensions && order.packageDimensions.length > 0) return order.packageDimensions;
  return (order.suggestedPackages ?? []).map((s) => ({
    kg: s.kg ?? 0,
    l: s.l ?? 0,
    a: s.a ?? 0,
    p: s.p ?? 0,
  }));
}

function fmtNum(n: number): string {
  return n.toLocaleString("it-IT", { maximumFractionDigits: 1 });
}

/** «3 colli · 61 kg · bancale»: la risposta che evita di aprire il pannello.
 *  Ogni pezzo cade se non lo sappiamo — «0 kg» su un collo non misurato
 *  sarebbe una misura inventata. */
export function riassuntoColli(order: Order): string {
  const colli = colliRegistrati(order);
  const n = order.colli > 0 ? order.colli : colli.length;
  const kg = colli.reduce((tot, c) => tot + (c.kg > 0 ? c.kg : 0), 0);
  const bancale = order.usePallet === true || order.usaBancale === true;
  return (
    [
      n > 0 ? `${n} ${n === 1 ? "collo" : "colli"}` : null,
      kg > 0 ? `${fmtNum(kg)} kg` : null,
      bancale ? "bancale" : null,
    ]
      .filter(Boolean)
      .join(" · ") || "da misurare"
  );
}

/** Misure di un collo in sola lettura: «100×50×30», o niente se non ci sono.
 *  Zero = non misurato, non «zero centimetri». */
function misureCollo(c: Collo): string | null {
  return c.l > 0 && c.a > 0 && c.p > 0 ? `${fmtNum(c.l)}×${fmtNum(c.a)}×${fmtNum(c.p)}` : null;
}

/** I colli di un ordine gia' partito: si leggono, non si toccano. Riaprirli in
 *  editor e' un gesto esplicito («Modifica») — su un ordine spedito cambiare le
 *  misure non cambia il pacco, cambia solo cio' che raccontiamo di lui. */
function ColliSoloLettura({ colli }: { colli: Collo[] }) {
  if (colli.length === 0) {
    return <div className="text-[12px] text-ink">Nessuna misura registrata.</div>;
  }
  return (
    <div className="flex flex-col gap-1">
      {colli.map((c, i) => (
        <div
          key={`collo-${i}`}
          className="flex items-baseline justify-between gap-3 border-b border-line pb-1 text-[12.5px] last:border-b-0"
        >
          <span className="font-mono text-[11px] text-ink">Collo {i + 1}</span>
          <span className="flex items-baseline gap-3 tabular-nums text-ink">
            <span>{c.kg > 0 ? `${fmtNum(c.kg)} kg` : "peso non misurato"}</span>
            <span className="font-mono text-[11px] text-ink">{misureCollo(c) ?? "—"}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

interface ShippingSectionProps {
  order: Order;
  onChanged: () => void;
  /** Serve a «Salva e rifai la LDV» dentro l'editor colli. Assente → il
   *  tasto non c'e'. «Genera / Rifai LDV» stanno nella tappa Etichetta. */
  onGenerateLabel?: (order: Order, opts?: GenerateLabelClickOpts) => void | Promise<void>;
  labelBusy?: boolean;
  /** Il draft dell'editor colli, condiviso con la tappa Etichetta: «Genera
   *  LDV» lassu' usa le misure scritte qui, anche senza «Salva». */
  draftRef?: MutableRefObject<ShippingDraft>;
  /** Il pannello tracking ha letto «Consegnata» dal vivo. */
  onConsegnata?: () => void;
}

/** Spedizione dell'ordine: tracking live per-collo + editor colli (ha il suo
 *  Salva). Dal 2026-10-23 qui non ci sono piu' i tasti dell'ordine: vivono
 *  nelle tappe del percorso, ognuno accanto al passo che fa avanzare.
 *
 *  Tracking per-collo (Piano 4): ``TrackingPanel`` monta qui, con i colli
 *  caricati via ``fetchOrderParcelCodes`` al mount di questa sezione. */
export function ShippingSection({
  order,
  onChanged,
  onGenerateLabel,
  labelBusy = false,
  draftRef: draftRefEsterno,
  onConsegnata,
}: ShippingSectionProps) {
  const draftRefProprio = useRef<ShippingDraft>(EMPTY_DRAFT);
  const draftRef = draftRefEsterno ?? draftRefProprio;
  // Il draft vive in un ref (non serve ri-renderizzare a ogni tasto), ma il
  // solo `dirty` sì: da lui dipendono il tasto «Salva e rifai la LDV» e
  // l'avviso sul collassabile. Letto dal ref nel JSX non comparirebbero mai.
  const [draftDirty, setDraftDirty] = useState(false);
  const handleDraftChange = useCallback(
    (draft: ShippingDraft) => {
      draftRef.current = draft;
      setDraftDirty(draft.dirty);
    },
    [draftRef],
  );
  const isAllService = orderHasAllServiceSku(order);
  const hasLabel = haLdv(order);
  // Il tasto contestuale dentro l'editor: solo dove la LDV la facciamo noi
  // (AllService ha il suo xlsx) e solo se c'e' qualcosa da applicare.
  const salvaERifai = !isAllService && !!onGenerateLabel && draftDirty;

  // Colli per il tracking per-collo (Piano 4): estratti dal PDF LDV lato
  // backend (GET /orders/{ref}/parcels, solo SDA). Best-effort: se fallisce
  // il panel mostra comunque la LDV master.
  const [parcelCodes, setParcelCodes] = useState<string[]>([]);
  useEffect(() => {
    if (!order.trackingCode) return undefined;
    let cancelled = false;
    fetchOrderParcelCodes(order.id)
      .then((codes) => {
        if (cancelled) return;
        // Aggiorna solo su variazione REALE di contenuto: evita il churn di
        // identita' che farebbe ripartire la fetch di TrackingPanel, ma una
        // transizione legittima a vuoto (reship verso corriere non-SDA)
        // propaga e pulisce i colli della vecchia LDV.
        setParcelCodes((prev) =>
          prev.length === codes.length && prev.every((c, i) => c === codes[i]) ? prev : codes,
        );
      })
      .catch(() => {
        // ponytail: senza colli il panel degrada al solo master — nessun toast.
      });
    return () => {
      cancelled = true;
    };
  }, [order.id, order.trackingCode]);

  // Colli: a scomparsa, e in sola lettura su un ordine gia' partito — con
  // «Modifica» che riapre l'editor quando serve davvero.
  const colli = colliRegistrati(order);
  const riassunto = riassuntoColli(order);
  const [modifica, setModifica] = useState(false);
  const soloLettura =
    !modifica && (order.state === "spedito" || order.lastTrackingEvent?.delivered === true);

  return (
    <section className={`${PANEL} ${AREA_CLS.merce}`}>
      <h3 className={`mb-3 ${TITLE}`}>
        Spedizione{" "}
        <span className={TITLE_NOTE}>
          {nomeCorriere(order.corriere)}
          {order.colli > 0 && ` · ${order.colli} ${order.colli === 1 ? "collo" : "colli"}`}
        </span>
      </h3>
      <RitiroRicambio order={order} />
      {order.trackingCode ? (
        <div className="mb-3">
          <TrackingPanel
            carrier={order.corriere || ""}
            trackingCode={order.trackingCode}
            parcelCodes={parcelCodes}
            onOpenExternal={openExternalUrl}
            compatto
            onConsegnata={onConsegnata}
          />
        </div>
      ) : (
        <p className="mb-3 rounded-lg bg-paper px-3 py-2.5 text-[13px] text-ink">
          Nessun tracking: la LDV non è ancora stata fatta.
        </p>
      )}
      {order.ldvCode && order.ldvCode !== order.trackingCode && (
        <div className="mb-3 flex items-center gap-2 text-[13px] text-ink">
          <span className="font-semibold">LDV</span>
          <span className="font-mono text-[12.5px]">{order.ldvCode}</span>
        </div>
      )}
      {/* Colli e misure a scomparsa: il riassunto nel titolo e' la ragione per
          cui si puo' tenere chiuso. Ambra a draft sporco: chiuso il
          collassabile l'editor si smonta ma il draft resta, e «Genera LDV»
          userebbe valori che non si vedono piu'. */}
      <Collapsible
        id="colli"
        title="Colli e misure"
        summary={draftDirty ? `${riassunto} · modifiche non applicate` : riassunto}
        tone={draftDirty ? "amber" : "neutral"}
        annidato
      >
        {soloLettura ? (
          <>
            <ColliSoloLettura colli={colli} />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[12px] text-ink">Registrati: {riassunto}</span>
              <button type="button" onClick={() => setModifica(true)} className={BTN_SM}>
                Modifica
              </button>
            </div>
          </>
        ) : (
          <>
            <ShippingEditor
              order={order}
              onSaved={onChanged}
              onDraftChange={handleDraftChange}
              // Un gesto solo (Davide 2026-09-03): «se modifico voglio
              // ricreare la ldv!». Compare solo a draft sporco.
              saveTone={salvaERifai ? "secondary" : "primary"}
              footerActions={
                salvaERifai && onGenerateLabel ? (
                  <button
                    type="button"
                    data-testid="save-and-label-btn"
                    disabled={labelBusy}
                    title="Salva le misure appena scritte e rigenera l'etichetta con quei valori"
                    onClick={() => void onGenerateLabel(order, opzioniLdv(draftRef.current, true))}
                    className={`${BTN_PRIMARY} w-full whitespace-nowrap`}
                  >
                    {labelBusy
                      ? "Genero…"
                      : hasLabel
                        ? "Salva e rifai la LDV"
                        : "Salva e genera la LDV"}
                  </button>
                ) : undefined
              }
            />
            {/* «Registrati» e non «Totale»: mentre si edita questi restano i
                numeri SALVATI, non la somma di quel che si sta scrivendo. */}
            <div className="mt-2 text-[12px] text-ink">Registrati: {riassunto}</div>
          </>
        )}
      </Collapsible>
    </section>
  );
}
